<template>
  <div ref="dropZoneRef" class="relative flex h-full min-h-0 w-full flex-col overflow-hidden">
    <DragDropOverlay :is-dragging="isOverDropZone" />

    <ChatHeader
      :contact-name="contactName"
      :contact-profile-name="contactProfileName"
      :contact-username="contactUsername"
      :campaign-name="chat.campaign_name"
      :channel-id="chat.channel_id"
      :is-typing="whatsAppCoordinator.chatStore.isChatTyping(chat.id)"
      :start-date="formattedStartDate"
      :last-message-date="formattedLastMessageDate"
      :message-count="visibleMessageCount"
      :show-back-button="true"
      @back-to-list="$emit('back-to-list')"
      @open-contact-info="$emit('open-contact-info')"
    />

    <div class="relative flex min-h-0 flex-1 flex-col">
      <MessageList
        :messages="messages"
        :is-loading-history="isLoadingHistory"
        :has-more-history="hasMoreHistory"
        :translation-enabled="isTranslationEnabled"
        :translation-language="agentLanguage"
        :translation-loading-message-ids="translationLoadingMessageIds"
        @reply="handleReply"
        @delete="handleDeleteMessage"
        @retry-translation="retryMessageTranslation"
        @translation-pivot="handleTranslationPivot"
        @load-history="$emit('load-history')"
      />

      <TranslationPreviewPanel
        :visible="showTranslationPreview && !showFilePreviewModal"
        :is-loading="isTranslationPreviewLoading"
        :translated-text="translationPreview"
      />

      <ChatFooter
        v-show="!showFilePreviewModal && !showCamera"
        ref="chatFooterRef"
        :chat-id="chat.id"
        :is-pending-chat="isPendingChat"
        :is-taking-chat="isTakingChat"
        :show-audio-recorder="showAudioRecorder"
        :is-signature-enabled="isSignatureEnabled"
        :is-translation-enabled="isTranslationEnabled"
        :is-translation-disabled="isTranslationDisabled"
        :agent-language="agentLanguage"
        :client-language="clientLanguage"
        :user-name="authStore.userName"
        :reply-to-message="replyToMessage"
        :is-outside24-hour-window="isOutside24HourWindow"
        :is-awaiting-client-reply="isAwaitingClientReply"
        :show-transfer-button="!isPendingChat"
        :is-closed-by-timeout="isClosedByTimeout"
        :timeout-campaign-name="timeoutCampaignName"
        :timeout-bot-id="timeoutBotId"
        :released-by-template-reason="releasedByTemplateReason"
        @send-message="sendMessage"
        @send-audio="handleSendAudio"
        @cancel-audio="handleCancelAudio"
        @start-audio-recording="startAudioRecording"
        @take-chat="$emit('take-chat')"
        @toggle-signature="toggleSignature"
        @toggle-translation="toggleTranslation"
        @update:agent-language="setAgentLanguage"
        @update:client-language="setClientLanguage"
        @open-camera="showCamera = true"
        @files-select="handleFilesSelectWrapper"
        @cancel-reply="handleCancelReply"
        @send-template="handleOpenTemplateDialog"
        @transfer-chat="$emit('transfer-chat')"
        @end-chat="showEndChatDialog = true"
        @close-chat="handleTimeoutClose"
        @close-bulk-template-chat="handleBulkTemplateClose"
      />

      <CameraCapture
        v-if="showCamera"
        :key="chat.id"
        @close="closeCamera"
        @capture="handleCameraCapture"
      />

      <FilePreviewModal
        v-model:visible="showFilePreviewModal"
        :files="previewFiles"
        :category="previewCategory"
        @send="handleSendFilesWithCaption"
        @close="closeFilePreview"
      />
    </div>

    <!-- End Chat Confirmation Dialog -->
    <AppDialog
      v-if="canEndChat"
      v-model="showEndChatDialog"
      customClass="backdrop-blur-xl"
      modal
      :draggable="false"
      header="Confirmar finalización"
      width="30rem"
    >
      <template #header>
        <label class="font-semibold text-lg">{{ $t('whatsapp.end_chat_confirmation') }}</label>
      </template>
      <label class="text-sm">{{ $t('whatsapp.finish_chat_question') }}</label>

      <template #footer>
        <AppButtonIcon
          label="Cancelar"
          severity="secondary"
          @click="showEndChatDialog = false"
          autofocus
        />
        <AppButtonIcon label="Finalizar Chat" @click="handleConfirmEndChat" />
      </template>
    </AppDialog>

    <!-- Expired Chat Dialog -->
    <ExpiredChatDialog
      v-model:visible="showTemplateDialog"
      :initial-contact="templateDialogContact"
      :initial-campaign-id="chat.campaign_id"
      :incoming-route-id="chat.incoming_route_id"
      :chat-id="chat.id"
      @send="handleSendTemplate"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onUnmounted, nextTick } from 'vue'
import { useToast } from 'primevue/usetoast'
import { useI18n } from 'vue-i18n'
import { useWhatsAppCoordinator } from '../../stores/useWhatsAppCoordinator'
import { useAuthStore } from '@/stores/useAuthStore'
import { useWorkspaceStore } from '@/stores/useWorkspaceStore'
import { AI_DISABLED_MESSAGE, isAiDisabledError } from '@/utils/aiDisabledError'
import { useChatDraftStore } from '../../stores/useChatDraftStore'
import { useFiles, type FilePreviewCategory } from '../../composables/useFiles'
import { useMessageWindow } from '../../composables/useMessageWindow'
import { parseUtcDate } from '../../composables/useTimeFormatter'
import { MessageSourceType, MessageType } from '../../types/enums'
import { useMessageTranslationService } from '../../services/useMessageTranslationService'
import ChatHeader from './ChatHeader.vue'
import MessageList from './MessageList.vue'
import CameraCapture from './CameraCapture.vue'
import AppButton from '@/components/atoms/buttons/AppButton.vue'
import ChatFooter from './ChatFooter.vue'
import TranslationPreviewPanel from '../../molecules/message-translation/TranslationPreviewPanel.vue'
import DragDropOverlay from './DragDropOverlay.vue'
import FilePreviewModal from '../dialogs/FilePreviewModal.vue'
import ExpiredChatDialog from '../dialogs/ExpiredChatDialog.vue'
import type {
  Message,
  MessageContent,
  ContactSelection,
  Template,
  RoutingDestination,
  TemplateSendComponent,
} from '../../types/interfaces'
import AppDialog from '@/components/atoms/dialogs/AppDialog.vue'
import AppButtonIcon from '@/components/atoms/buttons/AppButtonIcon.vue'

interface Props {
  contactName: string
  contactProfileName?: string | null
  contactUsername?: string | null
  contactPhone: string
  messages: Message[]
  isTakingChat?: boolean
  isLoadingHistory?: boolean
  hasMoreHistory?: boolean
  chat: {
    id: string
    user_id: string | null
    status: string
    campaign_id?: string
    campaign_name?: string
    channel_id?: string
    incoming_route_id: string
    start_date?: string
    last_message_date?: string
  }
  currentUserId: string
}

const props = defineProps<Props>()
const emit = defineEmits<{
  'send-message': [
    data: {
      // El chat en el que se escribio, no el que este abierto al llegar aqui:
      // entre el envio y la respuesta de la traduccion el agente pudo cambiar.
      chatId: string
      text: string
      // Lo que el agente escribio de verdad, sin firma ni traduccion encima.
      // Es lo que se le devuelve al borrador si el envio no llega a salir.
      draftText: string
      replyToId?: string
      aiTranslate?: MessageContent['ai_translate']
    },
  ]
  'send-file': [
    data: {
      // Igual que en `send-message`: el chat donde se adjunto. El fichero se
      // lee en `FileReader`, asi que el emit sale un tick despues.
      chatId: string
      base64: string
      fileName: string
      contentType: string
      extension: string
      caption?: string
      isVoiceNote?: boolean
    },
  ]
  'send-template': [
    data: {
      contacts: ContactSelection[]
      template: Template
      parameters: TemplateSendComponent[] | null
      destination: RoutingDestination
      campaign_id?: string
      incoming_route_id?: string
      chat_id?: string
      is_bulk?: boolean
    },
  ]
  'transfer-chat': []
  'end-chat': []
  'close-chat': []
  'take-chat': []
  'back-to-list': []
  'load-history': []
  'open-contact-info': []
}>()

const whatsAppCoordinator = useWhatsAppCoordinator()
const draftStore = useChatDraftStore()
const authStore = useAuthStore()
const workspace = useWorkspaceStore()
const messageTranslationService = useMessageTranslationService()
const toast = useToast()
const { t } = useI18n()
const TRANSLATION_SCROLL_DEBOUNCE_MS = 800
const TRANSLATION_BATCH_COOLDOWN_MS = 1500
const TRANSLATION_PREVIEW_DEBOUNCE_MS = 1000

const formatHeaderDate = (raw: string | undefined): string => {
  if (!raw) return '-'
  const d = parseUtcDate(raw)
  if (Number.isNaN(d.getTime())) return '-'
  return d.toLocaleDateString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const formattedStartDate = computed(() => formatHeaderDate(props.chat.start_date))
const formattedLastMessageDate = computed(() => formatHeaderDate(props.chat.last_message_date))
const visibleMessageCount = computed(
  () =>
    props.messages.filter(
      (m) =>
        (m.source_type === MessageSourceType.ENTRANTE ||
          m.source_type === MessageSourceType.SALIENTE ||
          m.source_type === MessageSourceType.TEMPLATE) &&
        m.content.type !== MessageType.INTERACTION,
    ).length,
)

const chatFooterRef = ref()
const showCamera = ref(false)
const showAudioRecorder = ref(false)
const showEndChatDialog = ref(false)
const showTemplateDialog = ref(false)
const replyToMessage = ref<Message | null>(null)
const translationPreview = ref('')
const isTranslationPreviewLoading = ref(false)
const translationLoadingMessageIds = ref<string[]>([])
const translationInFlightIds = ref(new Set<string>())
const isAiDisabledForChat = ref(false)
const failedTranslationText = ref<string | null>(null)
let translationPreviewTimer: ReturnType<typeof setTimeout> | null = null
let translationPreviewRequestId = 0
// El envio lleva su propio contador. Compartirlo con la vista previa hacia que
// cualquier cosa que reprograme una previa —escribir, o cambiar de chat, que
// ahora cambia el texto del editor porque cada chat trae su borrador— cancelara
// en silencio un envio ya confirmado que estaba esperando la traduccion.
let messageSendRequestId = 0
let translationPivotTimer: ReturnType<typeof setTimeout> | null = null
let translationCooldownTimer: ReturnType<typeof setTimeout> | null = null
let hasPendingTranslationRecalculation = false
let isTranslationBatchRunning = false
let lastTranslationBatchStartedAt = 0
let pendingTranslationPivotId: string | null = null

// Message window validation
const { isOutside24HourWindow, isAwaitingClientReply } = useMessageWindow(() => props.chat.status)

const isPendingChat = computed(() => {
  return props.chat.user_id !== props.currentUserId
})

// Finalizar es propio del chat que se atiende, salvo el EXPIRE: ese ya no
// recibe nada del cliente y se cierra igual estando en cola, sin tomarlo antes.
// Sin esto el dialogo de confirmacion no se montaba y el boton no hacia nada.
const canEndChat = computed(() => !isPendingChat.value || isOutside24HourWindow.value)

const isSignatureEnabled = computed(() => {
  return whatsAppCoordinator.chatStore.isChatSignatureEnabled(props.chat.id)
})

const isTranslationEnabled = computed(() => {
  return whatsAppCoordinator.chatStore.isChatTranslationEnabled(props.chat.id)
})
const isTranslationDisabled = computed(
  () =>
    isAiDisabledForChat.value ||
    Boolean(
      props.chat.campaign_id && workspace.campaignUseIaById[props.chat.campaign_id] === false,
    ),
)

const hasPendingComposerText = computed(() => {
  const text = getCurrentInputText()
  return Boolean(text && text.trim().length > 0)
})

const showTranslationPreview = computed(() => {
  return (
    isTranslationEnabled.value &&
    !isTranslationDisabled.value &&
    hasPendingComposerText.value &&
    (isTranslationPreviewLoading.value || Boolean(translationPreview.value.trim()))
  )
})

const agentLanguage = computed(() =>
  whatsAppCoordinator.chatStore.getChatAgentLanguage(props.chat.id),
)
const clientLanguage = computed(() =>
  whatsAppCoordinator.chatStore.getChatClientLanguage(props.chat.id),
)

const timeoutClosure = computed(() =>
  whatsAppCoordinator.chatStore.getChatTimeoutClosure(props.chat.id),
)

// Solo mostrar banner si:
// - Redirigido a campaña a la que el usuario NO pertenece
// - Redirigido a un bot
// - Chat finalizado
// Si la campaña destino SÍ está en el mapa del usuario, isPendingChat mostrará "tomar chat"
const isClosedByTimeout = computed(() => {
  if (!timeoutClosure.value) return false
  if (timeoutClosure.value.campaignId) {
    return !whatsAppCoordinator.chatStore.campaignMap.has(timeoutClosure.value.campaignId)
  }
  return true
})

const releasedByTemplateReason = computed(() =>
  whatsAppCoordinator.chatStore.getChatBulkTemplateReleaseReason(props.chat.id),
)

const canWriteInChat = computed(
  () =>
    !showAudioRecorder.value &&
    !isClosedByTimeout.value &&
    !isPendingChat.value &&
    !isOutside24HourWindow.value &&
    !isAwaitingClientReply.value,
)

const timeoutCampaignName = computed(() => {
  if (!timeoutClosure.value?.campaignId) return null
  return whatsAppCoordinator.chatStore.getCampaign(timeoutClosure.value.campaignId)?.name ?? null
})

const timeoutBotId = computed(() => timeoutClosure.value?.botId ?? null)

const handleTimeoutClose = () => {
  const chatId = props.chat.id
  whatsAppCoordinator.chatStore.setChatTimeoutClosure(chatId, null)
  whatsAppCoordinator.chatStore.removeChat(chatId)
  emit('close-chat')
}

const handleBulkTemplateClose = () => {
  const chatId = props.chat.id
  whatsAppCoordinator.chatStore.setChatBulkTemplateRelease(chatId, null)
  whatsAppCoordinator.chatStore.removeChat(chatId)
  emit('close-chat')
}

onUnmounted(() => {
  clearTranslationPivotTimer()
  clearTranslationCooldownTimer()
  clearTranslationPreviewTimer()
})

// Contact for template dialog
const templateDialogContact = computed<ContactSelection>(() => ({
  phone: props.contactPhone,
  name: props.contactName,
  campaign_id: props.chat.campaign_id,
}))

// File handling composable
const {
  dropZoneRef,
  isOverDropZone,
  previewFiles,
  previewCategory,
  showFilePreviewModal,
  selectFiles,
  closeFilePreview,
} = useFiles()

const clearSentMessage = (
  messageInputComponent: any,
  messageTextRef: any,
  chatIdAtSend: string,
) => {
  // Enviado es enviado: el borrador del chat al que fue el mensaje se borra
  // aunque el agente ya este mirando otro.
  draftStore.clearDraft(chatIdAtSend)

  // Lo demas es del editor visible. Si el agente cambio de chat mientras volvia
  // la traduccion, lo que hay en pantalla es otra conversacion y no se toca; el
  // watcher de `chat.id` ya limpio la traduccion.
  if (props.chat.id !== chatIdAtSend) return

  translationPreviewRequestId++
  clearTranslationPreviewTimer()
  translationPreview.value = ''
  isTranslationPreviewLoading.value = false

  if (typeof messageTextRef === 'string') {
    messageInputComponent.messageText = ''
  } else {
    messageTextRef.value = ''
  }

  if (chatIdAtSend) {
    draftStore.clearDraft(chatIdAtSend)
  }

  messageInputComponent.resetTextareaHeight?.()
  replyToMessage.value = null
}

const sendMessage = async () => {
  const messageInputComponent = chatFooterRef.value?.messageInputRef
  if (!messageInputComponent) return

  const messageTextRef = messageInputComponent.messageText
  const messageText = typeof messageTextRef === 'string' ? messageTextRef : messageTextRef.value
  const chatIdAtSend = props.chat.id

  if (messageText && messageText.trim()) {
    let finalMessage = messageText.trim()

    if (isSignatureEnabled.value) {
      finalMessage = `${finalMessage}\n> ${authStore.userName}`
    }

    const replyToId = replyToMessage.value?.id || undefined
    const shouldSendOriginal = failedTranslationText.value === finalMessage

    if (isTranslationEnabled.value && !isTranslationDisabled.value && !shouldSendOriginal) {
      const sendId = ++messageSendRequestId
      // La previa en vuelo ya no pinta nada: el agente le dio a enviar.
      translationPreviewRequestId++
      clearTranslationPreviewTimer()
      isTranslationPreviewLoading.value = true

      try {
        const response = await messageTranslationService.previewTranslation({
          campaign_id: props.chat.campaign_id || '',
          text: finalMessage,
          target_language: clientLanguage.value,
        })
        if (sendId !== messageSendRequestId) return

        const translatedMessage = response.translated_content.trim()
        if (!translatedMessage) throw new Error('Empty translation')

        translationPreview.value = translatedMessage
        failedTranslationText.value = null
        const originalMessageTranslation = {
          [agentLanguage.value]: {
            translated_content: finalMessage,
            status: 'ready' as const,
          },
        }
        emit('send-message', {
          chatId: chatIdAtSend,
          text: translatedMessage,
          draftText: messageText,
          replyToId,
          aiTranslate: originalMessageTranslation,
        })
        clearSentMessage(messageInputComponent, messageTextRef, chatIdAtSend)
        return
      } catch (error) {
        if (sendId !== messageSendRequestId) return
        failedTranslationText.value = finalMessage
        if (isAiDisabledError(error)) handleAiDisabled()
        toast.add({
          severity: 'warn',
          summary: t('whatsapp.translation_failed'),
          detail: t('whatsapp.translation_failed_send_original'),
          life: 4000,
        })
        return
      } finally {
        // El indicador es del chat visible: si el agente ya se fue a otro, la
        // previa de ese chat manda y esta no la pisa.
        if (sendId === messageSendRequestId && props.chat.id === chatIdAtSend) {
          isTranslationPreviewLoading.value = false
        }
      }
    }

    emit('send-message', {
      chatId: chatIdAtSend,
      text: finalMessage,
      draftText: messageText,
      replyToId,
    })
    failedTranslationText.value = null
    clearSentMessage(messageInputComponent, messageTextRef, chatIdAtSend)
  }
}

const getCurrentInputText = () => {
  const messageTextRef = chatFooterRef.value?.messageText
  return typeof messageTextRef === 'string' ? messageTextRef : messageTextRef?.value || ''
}

const clearTranslationPreviewTimer = () => {
  if (translationPreviewTimer) {
    clearTimeout(translationPreviewTimer)
    translationPreviewTimer = null
  }
}

const handleAiDisabled = () => {
  isAiDisabledForChat.value = true
  if (props.chat.campaign_id) workspace.setCampaignUseIa(props.chat.campaign_id, false)
  workspace.setUseIa(false)
  if (isTranslationEnabled.value) {
    whatsAppCoordinator.chatStore.toggleChatTranslation(
      props.chat.id,
      agentLanguage.value,
      clientLanguage.value,
    )
  }
  clearTranslationPivotTimer()
  clearTranslationCooldownTimer()
  clearTranslationPreviewTimer()
  translationInFlightIds.value = new Set()
  translationLoadingMessageIds.value = []
  translationPreview.value = AI_DISABLED_MESSAGE
  isTranslationPreviewLoading.value = false
}

const scheduleTranslationPreview = (text: string) => {
  clearTranslationPreviewTimer()
  translationPreview.value = ''
  isTranslationPreviewLoading.value = false

  const trimmedText = text?.trim() || ''
  if (!isTranslationEnabled.value || isTranslationDisabled.value || !trimmedText) return

  const requestId = ++translationPreviewRequestId
  isTranslationPreviewLoading.value = true
  translationPreviewTimer = setTimeout(async () => {
    try {
      const response = await messageTranslationService.previewTranslation({
        campaign_id: props.chat.campaign_id || '',
        text: trimmedText,
        target_language: clientLanguage.value,
      })
      if (requestId === translationPreviewRequestId) {
        translationPreview.value = response.translated_content
      }
    } catch (error) {
      if (requestId === translationPreviewRequestId) {
        if (isAiDisabledError(error)) {
          handleAiDisabled()
        } else {
          translationPreview.value = ''
        }
      }
    } finally {
      if (requestId === translationPreviewRequestId) {
        isTranslationPreviewLoading.value = false
      }
    }
  }, TRANSLATION_PREVIEW_DEBOUNCE_MS)
}

const toggleSignature = () => {
  whatsAppCoordinator.chatStore.toggleChatSignature(props.chat.id)
}

const clearTranslationPivotTimer = () => {
  if (translationPivotTimer) {
    clearTimeout(translationPivotTimer)
    translationPivotTimer = null
  }
}

const clearTranslationCooldownTimer = () => {
  if (translationCooldownTimer) {
    clearTimeout(translationCooldownTimer)
    translationCooldownTimer = null
  }
}

const isAutoTranslationCandidate = (message: Message) => {
  if (message.content.type !== MessageType.TEXT) return false
  if (translationInFlightIds.value.has(message.id)) return false
  const translation = message.content.ai_translate?.[agentLanguage.value]
  return translation?.status !== 'ready' && translation?.status !== 'error'
}

const buildTranslationWindow = (pivotMessageId: string) => {
  const pivotIndex = props.messages.findIndex((message) => message.id === pivotMessageId)
  if (pivotIndex === -1) return []

  const selected = new Set<string>()
  const addCandidate = (index: number) => {
    const message = props.messages[index]
    if (!message || selected.has(message.id) || !isAutoTranslationCandidate(message)) return false
    selected.add(message.id)
    return true
  }

  addCandidate(pivotIndex)

  for (let index = pivotIndex - 1; index >= 0 && selected.size < 10; index--) {
    addCandidate(index)
  }

  for (let index = pivotIndex + 1; index < props.messages.length && selected.size < 20; index++) {
    addCandidate(index)
  }

  for (let index = pivotIndex - 1; index >= 0 && selected.size < 20; index--) {
    addCandidate(index)
  }

  const order = new Map(props.messages.map((message, index) => [message.id, index]))
  return [...selected].sort((a, b) => (order.get(a) ?? 0) - (order.get(b) ?? 0))
}

const applyTranslationSyncResponse = (
  response: Awaited<ReturnType<typeof messageTranslationService.syncChatTranslationWindow>>,
) => {
  whatsAppCoordinator.messageStore.applyMessageTranslations(response.translated)
  response.failed.forEach((item) => {
    whatsAppCoordinator.messageStore.applyMessageTranslation(item.message_id, {
      [agentLanguage.value]: {
        translated_content: '',
        status: 'error',
      },
    })
  })
}

const runTranslationWindowBatch = async (pivotMessageId: string) => {
  if (!isTranslationEnabled.value || isTranslationDisabled.value) return
  if (isTranslationBatchRunning) {
    hasPendingTranslationRecalculation = true
    pendingTranslationPivotId = pivotMessageId
    return
  }

  const elapsedSinceLastBatch = Date.now() - lastTranslationBatchStartedAt
  if (elapsedSinceLastBatch < TRANSLATION_BATCH_COOLDOWN_MS) {
    clearTranslationCooldownTimer()
    translationCooldownTimer = setTimeout(() => {
      void runTranslationWindowBatch(pivotMessageId)
    }, TRANSLATION_BATCH_COOLDOWN_MS - elapsedSinceLastBatch)
    return
  }

  const messageIds = buildTranslationWindow(pivotMessageId)
  if (!messageIds.length) return

  isTranslationBatchRunning = true
  hasPendingTranslationRecalculation = false
  lastTranslationBatchStartedAt = Date.now()
  translationInFlightIds.value = new Set([...translationInFlightIds.value, ...messageIds])
  translationLoadingMessageIds.value = [...translationInFlightIds.value]

  try {
    const response = await messageTranslationService.syncChatTranslationWindow(props.chat.id, {
      target_language: agentLanguage.value,
      message_ids: messageIds,
    })
    applyTranslationSyncResponse(response)
  } catch (error) {
    if (isAiDisabledError(error)) {
      handleAiDisabled()
    }
  } finally {
    const nextInFlightIds = new Set(translationInFlightIds.value)
    messageIds.forEach((messageId) => nextInFlightIds.delete(messageId))
    translationInFlightIds.value = nextInFlightIds
    translationLoadingMessageIds.value = [...nextInFlightIds]
    isTranslationBatchRunning = false

    if (hasPendingTranslationRecalculation) {
      const nextPivotMessageId = pendingTranslationPivotId
      hasPendingTranslationRecalculation = false
      pendingTranslationPivotId = null
      if (nextPivotMessageId) {
        scheduleTranslationWindow(nextPivotMessageId)
      }
    }
  }
}

const scheduleTranslationWindow = (pivotMessageId: string) => {
  if (!isTranslationEnabled.value || isTranslationDisabled.value) return
  clearTranslationPivotTimer()
  translationPivotTimer = setTimeout(() => {
    void runTranslationWindowBatch(pivotMessageId)
  }, TRANSLATION_SCROLL_DEBOUNCE_MS)
}

const handleTranslationPivot = (messageId: string) => {
  scheduleTranslationWindow(messageId)
}

const toggleTranslation = async () => {
  if (isTranslationDisabled.value) return
  whatsAppCoordinator.chatStore.toggleChatTranslation(
    props.chat.id,
    agentLanguage.value,
    clientLanguage.value,
  )
}

const setAgentLanguage = (language: string) => {
  whatsAppCoordinator.chatStore.setChatTranslationLanguages(props.chat.id, {
    agentLanguage: language,
  })
  failedTranslationText.value = null
}

const setClientLanguage = (language: string) => {
  whatsAppCoordinator.chatStore.setChatTranslationLanguages(props.chat.id, {
    clientLanguage: language,
  })
  failedTranslationText.value = null
}

const retryMessageTranslation = async (message: Message) => {
  try {
    const response = await messageTranslationService.translateMessage(message.id, {
      target_language: agentLanguage.value,
    })
    whatsAppCoordinator.messageStore.applyMessageTranslation(
      response.message_id,
      response.ai_translate,
    )
  } catch (error) {
    if (isAiDisabledError(error)) {
      handleAiDisabled()
    }
    whatsAppCoordinator.messageStore.applyMessageTranslation(message.id, {
      [agentLanguage.value]: {
        translated_content: '',
        status: 'error',
      },
    })
  }
}

watch(
  () => props.chat.id,
  async () => {
    showCamera.value = false
    isAiDisabledForChat.value = false
    // La cita de respuesta es de un mensaje del chat anterior: sigue al texto,
    // que ahora se guarda por chat.
    replyToMessage.value = null
    clearTranslationPivotTimer()
    clearTranslationCooldownTimer()
    clearTranslationPreviewTimer()
    translationPreview.value = ''
    failedTranslationText.value = null
    isTranslationPreviewLoading.value = false
    translationInFlightIds.value = new Set()
    translationLoadingMessageIds.value = []
    isTranslationBatchRunning = false
    hasPendingTranslationRecalculation = false
    pendingTranslationPivotId = null
  },
  { immediate: true },
)

watch(
  canWriteInChat,
  (canWrite) => {
    if (canWrite) return
    whatsAppCoordinator.chatStore.setChatTranslationEnabled(
      props.chat.id,
      false,
      agentLanguage.value,
      clientLanguage.value,
    )
  },
  { immediate: true },
)

watch(
  [
    () => getCurrentInputText(),
    () => isTranslationEnabled.value,
    () => clientLanguage.value,
    () => props.chat.id,
  ],
  ([text, isEnabled, , chatId], [, , , previousChatId]) => {
    // Si cambia de chat, o traducción desactivada, o texto vacío: limpiar inmediatamente
    if (chatId !== previousChatId || !isEnabled || !text?.trim()) {
      clearTranslationPreviewTimer()
      translationPreview.value = ''
      isTranslationPreviewLoading.value = false
      if (chatId !== previousChatId || !isEnabled) return
    }

    scheduleTranslationPreview(text)
  },
)

const startAudioRecording = () => {
  showAudioRecorder.value = true
}

const handleSendAudio = async (data: { blob: Blob; duration: number }) => {
  const chatIdAtSend = props.chat.id

  try {
    const { blob: audioBlob, duration } = data

    if (!audioBlob || audioBlob.size === 0) {
      return
    }

    const MAX_AUDIO_SIZE = 16 * 1024 * 1024 // 16 MB — Meta limit
    if (audioBlob.size > MAX_AUDIO_SIZE) {
      alert('El audio supera el tamaño máximo permitido de 16 MB')
      return
    }

    const reader = new FileReader()
    reader.readAsDataURL(audioBlob)
    reader.onloadend = () => {
      const base64 = reader.result as string
      const base64Data = base64.split(',')[1]

      // Always send as webm/opus — backend will convert to ogg/opus for Meta
      const fileName = `audio-${Date.now()}.webm`

      emit('send-file', {
        chatId: chatIdAtSend,
        base64: base64Data,
        fileName: fileName,
        contentType: 'audio/webm; codecs=opus',
        extension: 'webm',
        caption: `Audio (${Math.floor(duration)}s)`,
        isVoiceNote: true,
      })
    }
  } catch {
    // Error al enviar audio
  }

  showAudioRecorder.value = false
}

const handleCancelAudio = () => {
  showAudioRecorder.value = false
}

const handleConfirmEndChat = () => {
  showEndChatDialog.value = false
  emit('end-chat')
}

const closeCamera = () => {
  showCamera.value = false
  nextTick(() => chatFooterRef.value?.focusMessageInput?.())
}

const handleCameraCapture = (file: File) => {
  showCamera.value = false
  handleFilesSelectWrapper([file], 'image')
}

const handleFilesSelectWrapper = (files: File[], category?: FilePreviewCategory) => {
  showCamera.value = false
  selectFiles(files, category)
}

const handleSendFilesWithCaption = (files: File[], captions: string[] | string) => {
  const chatIdAtSend = props.chat.id

  files.forEach((file, index) => {
    const caption = Array.isArray(captions) ? captions[index] : captions
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onloadend = () => {
      const base64 = reader.result as string
      const base64Data = base64.split(',')[1]
      const extension = file.name.split('.').pop() || ''

      emit('send-file', {
        chatId: chatIdAtSend,
        base64: base64Data,
        fileName: file.name,
        contentType: file.type,
        extension: extension,
        caption: caption || undefined,
      })
    }
  })

  closeFilePreview()
}

const handleReply = (message: Message) => {
  replyToMessage.value = message
  nextTick(() => {
    chatFooterRef.value?.focusMessageInput?.()
  })
}

const handleCancelReply = () => {
  replyToMessage.value = null
}

const handleDeleteMessage = () => {}

const handleOpenTemplateDialog = () => {
  showTemplateDialog.value = true
}

const handleSendTemplate = (data: {
  contacts: ContactSelection[]
  template: Template
  parameters: TemplateSendComponent[] | null
  destination: RoutingDestination
  campaign_id?: string
  incoming_route_id?: string
  chat_id?: string
  is_bulk?: boolean
}) => {
  emit('send-template', data)
}
</script>
